/**
 * Event names and classes shared between components. All events are dispatched on `document`.
 */

export class ThemeEvents {
  /** The cart changed: drawer, counters and listeners refresh */
  static cartUpdate = 'cart:update';
  /** An add to cart or cart change request failed */
  static cartError = 'cart:error';
  /** An overlay (drawer, search, sheet, zoom) opened */
  static overlayOpen = 'overlay:open';
  /** An overlay closed */
  static overlayClose = 'overlay:close';
  /** A slideshow changed slide */
  static slideshowSelect = 'slideshow:select';
}

/**
 * @typedef {Object} CartUpdateDetail
 * @property {number | null} itemCount Null when the response did not say
 * @property {Record<string, string>} [sections] Section HTML returned by the Ajax API (`sections` parameter)
 * @property {boolean} [openDrawer] Whether the cart drawer should open
 * @property {string} [source] Name of the component that changed the cart
 */

/** @extends {CustomEvent<CartUpdateDetail>} */
export class CartUpdateEvent extends CustomEvent {
  /** @param {CartUpdateDetail} detail */
  constructor(detail) {
    super(ThemeEvents.cartUpdate, { bubbles: true, detail });
  }
}

/**
 * @typedef {Object} CartErrorDetail
 * @property {string} message
 * @property {string} [source]
 */

/** @extends {CustomEvent<CartErrorDetail>} */
export class CartErrorEvent extends CustomEvent {
  /** @param {CartErrorDetail} detail */
  constructor(detail) {
    super(ThemeEvents.cartError, { bubbles: true, detail });
  }
}

/**
 * @typedef {Object} OverlayDetail
 * @property {HTMLElement} overlay
 */

/** @extends {CustomEvent<OverlayDetail>} */
export class OverlayOpenEvent extends CustomEvent {
  /** @param {HTMLElement} overlay */
  constructor(overlay) {
    super(ThemeEvents.overlayOpen, { bubbles: true, detail: { overlay } });
  }
}

/** @extends {CustomEvent<OverlayDetail>} */
export class OverlayCloseEvent extends CustomEvent {
  /** @param {HTMLElement} overlay */
  constructor(overlay) {
    super(ThemeEvents.overlayClose, { bubbles: true, detail: { overlay } });
  }
}

/**
 * @typedef {Object} SlideshowSelectEventData
 * @property {number} index
 * @property {string | null} id
 * @property {Element} slide
 * @property {number} previousIndex
 * @property {boolean} userInitiated
 * @property {'select' | 'scroll' | 'drag'} trigger
 */

/** @extends {CustomEvent<SlideshowSelectEventData>} */
export class SlideshowSelectEvent extends CustomEvent {
  /** @param {SlideshowSelectEventData} detail */
  constructor(detail) {
    super(ThemeEvents.slideshowSelect, { bubbles: true, detail });
  }
}
